import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { formatDate } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

import { SecurityAgreementFormService } from './security.agreement.form.service';
import { ConfirmService } from '../../shared/confirm/confirm.service';
import { AppService } from '../../app.service';
import { Formvalidator } from './security.agreement.formvalidator'
import { ValidationModalComponent } from '../../shared/views/ValidationModalComponent/validation.modal.component';

import customerJson from "../../../assets/config/customer.json";
import translateJson from "../../../translate.json";
import { AuthenticationService } from '../../auth';

@Component({
    selector: 'security-comp',
    providers: [SecurityAgreementFormService, Formvalidator, ConfirmService, AppService, AuthenticationService], 
    templateUrl: './security.agreement.form.component.html',  
    styleUrls: ['../security.agreement.css']  
})

export class SecurityAgreementFormComponent {   
  
    private activeComponent: boolean;
    private id: string;
    private type: string;
    private customerOrganisation: any;
    private subEmployers: Array<any> = []; // All employers except the customer organisation

    securityAgreementForm: FormGroup;

    @ViewChild('validationModal') validationModal: ValidationModalComponent;

    // Get translation/text from customerJson
    private i18 = translateJson.i18;
    private customerOrgId = customerJson.customer.organisationId;
    private countryCode = customerJson.customer.countryCode;

    constructor(
        private service: SecurityAgreementFormService,          
        private confirmService: ConfirmService,
        private location: Location,
        private activatedRoute: ActivatedRoute,
        private formValidator: Formvalidator,
        private appService: AppService,
        private authService: AuthenticationService) {
            this.authService.isAuthenticated(); 
            this.activeComponent = appService.isComponentActive('securityAgreement');
            this.id = this.activatedRoute.snapshot.paramMap.get('id');
            this.type = this.activatedRoute.snapshot.paramMap.get('type');
            if(this.id != null && this.type === 'edit') {
                this.setDefault();
                this.editSecurityAgreement();
            } else if (this.id === null && this.type === 'new') {
                this.setDefault();
                this.loadCustomerData();
            }
            this.setupSubEmployers();
        }

    /**
    * Default method that prepares the form
    */
    setDefault() {
        this.securityAgreementForm = new FormGroup({
            selectedCompany: new FormControl(null),
            id: new FormControl(),
            serialNumber: new FormControl(),
            clientName: new FormControl({disabled: false}),
            clientSecurityOfficer: new FormControl(),
            clientOrgNr: new FormControl({disabled: false}),
            clientStreetAddress: new FormControl({disabled: false}),
            clientPostalCode: new FormControl({disabled: false}),
            clientCity: new FormControl({disabled: false}),
            companyName: new FormControl({disabled: false}),
            organisationId: new FormControl(),
            companyStreetAddress: new FormControl(),
            companyPostalCode: new FormControl(),
            companyCity: new FormControl(),
            companySecurityOfficer: new FormControl(),
            createdDate: new FormControl(),
            createdBy: new FormControl(),
            securityAgreementStatus: new FormControl(),
            archived: new FormControl(),
            companyContactPerson: new FormControl(),
            clientContactPerson: new FormControl({disabled: false}),
            generateSequenceNumber: new FormControl(false),
            fasDiary: new FormControl(),

        });
        this.disableClientForms();
        this.disableCompanyForms();
    }

    /**
    * Validates a form the user want to save and sends it to backend through our service
    * @param fasFarm The form the user wants to save
    */
    saveSecurityAgreement(fasForm) {
        fasForm.clientName = this.securityAgreementForm.get("clientName").value;
        fasForm.clientOrgNr = this.securityAgreementForm.get("clientOrgNr").value;
        fasForm.clientStreetAddress = this.securityAgreementForm.get("clientStreetAddress").value;
        fasForm.clientPostalCode = this.securityAgreementForm.get("clientPostalCode").value;
        fasForm.clientContactPerson = this.securityAgreementForm.get("clientContactPerson").value;
        fasForm.clientCity = this.securityAgreementForm.get("clientCity").value;

        if(this.formValidator.validateFasForm(fasForm, this.validationModal.getModalArray())) {
            this.service.saveFas(fasForm).subscribe(
                data => this.appService.setRoute('securityAgreement/view/' + data.id),
                error => console.log(error.error)
            );
        } else {
            this.validationModal.pop();
        }
    }

    /**
    * Validates a form the user want to update and sends it to backend through our service
    * @param fasFarm The form the user wants to update
    */
    updateSecurityAgreement(fasForm) {
        fasForm.clientName = this.securityAgreementForm.get("clientName").value;
        fasForm.clientOrgNr = this.securityAgreementForm.get("clientOrgNr").value;
        fasForm.clientStreetAddress = this.securityAgreementForm.get("clientStreetAddress").value;
        fasForm.clientPostalCode = this.securityAgreementForm.get("clientPostalCode").value;
        fasForm.clientContactPerson = this.securityAgreementForm.get("clientContactPerson").value;
        fasForm.clientCity = this.securityAgreementForm.get("clientCity").value;

        if(this.formValidator.validateFasForm(fasForm, this.validationModal.getModalArray())) {
            this.service.updateFas(fasForm).subscribe(
                data => this.appService.setRoute('securityAgreement/view/' + data.id),
                error => console.log(error.error)
            );
        } else {
            this.validationModal.pop();
        }
    }

    /**
    * Method used to talk with the service and forwards the result
    */
    editSecurityAgreement() {
        this.service.getSecurityAgreementById(this.id)      
            .subscribe(
                data => this.setData(data),
                error =>  console.log(error)
            );
    }

    /**
    * Helper that is used when we want to edit the form. We take already existing data and patch 
    * our form with that data
    * @param data Data from backend
    */
    setData(data) {
        this.securityAgreementForm.patchValue({
            id: data.id,
            serialNumber: data.serialNumber,
            clientName: data.clientName,
            clientSecurityOfficer: data.clientSecurityOfficer,
            clientOrgNr: data.clientOrgNr,
            clientStreetAddress: data.clientStreetAddress,
            clientPostalCode: data.clientPostalCode,
            clientCity: data.clientCity,
            companyName: data.companyName,
            organisationId: data.organisationId,
            companyStreetAddress: data.companyStreetAddress,
            companyPostalCode: data.companyPostalCode,
            companyCity: data.companyCity,
            companySecurityOfficer: data.companySecurityOfficer,
            createdDate: formatDate(data.createdDate, 'yyyy-MM-dd', 'en'),
            createdBy: data.createdBy,
            securityAgreementStatus: data.securityAgreementStatus,
            archived: data.archived,
            companyContactPerson: data.companyContactPerson,
            clientContactPerson:  data.clientContactPerson,
            fasDiary: data.fasDiary,
        });
    }

    /**
    * Helper to quickly disable all fields the user should not interact with
    */
    disableClientForms() {
        this.securityAgreementForm.get("clientName").disable();
        this.securityAgreementForm.get("clientOrgNr").disable();
        this.securityAgreementForm.get("clientStreetAddress").disable();
        this.securityAgreementForm.get("clientPostalCode").disable();
        this.securityAgreementForm.get("clientCity").disable();
        this.securityAgreementForm.get("clientContactPerson").disable();
    }

    /**
     * Disable the fields that the user should not interact with.
     */
    disableCompanyForms() {
        this.securityAgreementForm.get("companyName").disable();
        this.securityAgreementForm.get("organisationId").disable();
    }

    /**
     * Fetches the customer organisation and fills the form with its data.
     */
    loadCustomerData(): void {
        this.service.getEmployerByOrgId(this.customerOrgId, this.countryCode).subscribe(
            data => {
                this.customerOrganisation = data;
                this.patchCustomerFields(this.customerOrganisation);
            },
            error => console.log(error)
        )
    }

    /**
     * Fills the form with the data of the given customer organisation.
     * @param customer A Supplier entity.
     */
    patchCustomerFields(customer: any): void {
        let contactPerson: string;
        if (this.hasContacts(customer)) {
            contactPerson = customer.contacts.find(contact => contact.contactType === 'SM').contactName;
        }

        this.securityAgreementForm.patchValue({
            clientName: customer.name,
            clientOrgNr: customer.organisationId,
            clientStreetAddress: customer.addresses[0].address_1,
            clientPostalCode: customer.addresses[0].postCode,
            clientCity: customer.addresses[0].city,
            clientContactPerson: contactPerson ? contactPerson : null,
            securityAgreementStatus: "Ej skickad" // TODO: fixa så den matchar de nya statusarna i REG-694,
        })
    }

    /**
     * Patches the company fields in the form with the data of the 
     * selected company.
     */
    patchCompanyFields() {
        const selectedCompany = this.securityAgreementForm.value.selectedCompany;
        
        if (selectedCompany) {
            let contactPerson: string;
            if (this.hasContacts(selectedCompany)) {
                contactPerson = selectedCompany.contacts.find(contact => contact.contactType === 'SM').contactName;
            }

            this.securityAgreementForm.patchValue({
                companyName: selectedCompany.name,
                organisationId: selectedCompany.organisationId,
                companyStreetAddress: selectedCompany.addresses[0].address_1,
                companyPostalCode: selectedCompany.addresses[0].postCode,
                companyCity: selectedCompany.addresses[0].city,
                companyContactPerson: contactPerson ? contactPerson : null
            })
        }        
    }

    /**
     * Fetches all employers and saves the subemployers in a global variable.
     */
    setupSubEmployers(): void {
        this.service.getAllEmployers().subscribe(
            (data: Array<any>) => this.subEmployers = data.filter(employer => employer.organisationId !== this.customerOrgId),
            error => console.log(error)
        )
    }

    /**
     * Returns a boolean value based on whether the company
     * has any stored contacts or not.
     * @param customer Supplier entity.
     */
    hasContacts(company: any): boolean {
        return company.contacts && company.contacts.length > 0;
    }

    /**
     * Go back to the previous view from where we came
     */
    back() {
        this.confirmService.confirm('Inga ändringar kommer sparas om du avbryter. Vill du fortsätta?')
            .then(result => {
                if(result) {
                    this.location.back();
                }
            });
    }

    /**
     * Toggle the generate diary functinality
    */
   customDiaryDisabler() {
    if (this.securityAgreementForm.value.generateSequenceNumber) {
        this.securityAgreementForm.get('fasDiary').disable();
        this.securityAgreementForm.patchValue({fasDiary: "Nummer genereras"});

    } else {
        this.securityAgreementForm.get('fasDiary').enable();
        this.securityAgreementForm.patchValue({fasDiary: null});
    }
            
}
}